/**
 * reCAPTCHA v3 loader shared by every form that posts to the contact API
 * (contact, merchant sign-up, rider application). No widget; a token is
 * fetched per submit.
 */

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}

let loadPromise: Promise<void> | null = null;

/**
 * Loads reCAPTCHA v3 once per page and resolves only when it can actually issue
 * tokens. api.js loads in two stages: on its own "load" event `grecaptcha`
 * exists but `execute` may not yet, so we wait for `grecaptcha.ready`, which
 * fires once the second stage is in. A failed load clears the cache so the
 * next call retries.
 */
export const loadRecaptchaV3 = (siteKey: string): Promise<void> => {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    const fail = () => reject(new Error("reCAPTCHA failed to load. Please try again."));

    const whenReady = (attempt = 0) => {
      if (window.grecaptcha?.ready) {
        window.grecaptcha.ready(() => resolve());
      } else if (attempt < 100) {
        window.setTimeout(() => whenReady(attempt + 1), 100);
      } else {
        fail();
      }
    };

    if (window.grecaptcha?.ready) return whenReady();

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.dataset.recaptcha = "v3";
    script.onload = () => whenReady();
    script.onerror = fail;
    document.head.appendChild(script);
  });

  loadPromise.catch(() => {
    loadPromise = null;
  });
  return loadPromise;
};

export const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

export const CONTACT_ENDPOINT =
  process.env.REACT_APP_CONTACT_ENDPOINT || "https://pabs.online/api/v1/contact";

/** Fetches a fresh token for the contact API's "contact" action. */
export const getRecaptchaToken = async (siteKey: string) => {
  await loadRecaptchaV3(siteKey);
  return window.grecaptcha!.execute(siteKey, { action: "contact" });
};
